'use client';

import { useCallback } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';

/**
 * Client-side image upload for chat attachments (Canny task 2B): presign via
 * `/api/editor-assets/upload/presign`, then `PUT` the file straight to S3.
 * Returns the public CloudFront URL (`presign.url`), which is what
 * `composeAgentExtras` forwards as `imageUrls` to `/api/editor-agent`.
 */
export function useAttachmentUpload() {
  const { authedFetch } = useAuthToken();

  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      const presignRes = await authedFetch('/api/editor-assets/upload/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, fileType: file.type, fileSize: file.size }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok || !presign?.uploadUrl || !presign?.url) {
        throw new Error(presign?.error || 'presign failed');
      }
      // Бэкенд возвращает точный набор заголовков под провайдера: Azure
      // требует x-ms-blob-type, S3 — Cache-Control, которым подписан URL.
      // Свой Content-Type ставим только когда бэкенд ничего не прислал.
      const headers: Record<string, string> =
        presign.headers && Object.keys(presign.headers).length > 0
          ? presign.headers
          : { 'Content-Type': file.type };

      const put = await fetch(presign.uploadUrl, { method: 'PUT', headers, body: file });
      // Azure Blob отвечает на успешную запись 201, а не 200 — `put.ok`
      // покрывает оба, но текст ошибки не должен врать про S3.
      if (!put.ok) {
        throw new Error(`не удалось загрузить файл (${put.status})`);
      }
      return presign.url as string;
    },
    [authedFetch]
  );

  /**
   * Client-side document extraction for chat attachments (Canny task 2C):
   * posts the file as multipart `FormData` to `/api/editor-agent/extract`
   * (Task 7), which returns `{ name, text }` with `text` already capped at
   * 20k chars server-side. Returns just the extracted text — that's what
   * `composeAgentExtras` forwards as `docTexts` to `/api/editor-agent`.
   *
   * `authedFetch` (see `useAuthToken.ts`) only force-sets
   * `Content-Type: application/json` when the body ISN'T a `FormData`
   * instance (`init.body instanceof FormData` is explicitly excluded), so
   * it's safe to reuse here as-is: it still attaches the Bearer token, and
   * the browser is left free to set its own `multipart/form-data; boundary=`
   * header for the `FormData` body. No need to drop to a raw `fetch()`.
   */
  const extractDoc = useCallback(
    async (file: File): Promise<string> => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authedFetch('/api/editor-agent/extract', { method: 'POST', body: fd });
      const json = await res.json();
      if (!res.ok || typeof json?.text !== 'string') throw new Error(json?.error || 'extract failed');
      return json.text as string;
    },
    [authedFetch]
  );

  return { uploadImage, extractDoc };
}
